import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { useProducts } from '@/hooks/useApi.js';
import { ProductCard } from '@/components/products/ProductCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const categoryTitles: Record<string, string> = {
  boys: 'Boys Collection',
  girls: 'Girls Collection', 
  ladies: 'Ladies Collection',
  men: 'Men Collection',
};

const SUB_CATEGORIES = {
  Boys: [
    { key: 'All', label: 'All' },
    { key: 'T-Shirts', label: 'T-Shirts' },
    { key: 'Shirts', label: 'Shirts' },
    { key: 'Jeans', label: 'Jeans' },
    { key: 'Shorts', label: 'Shorts' },
    { key: 'Pants', label: 'Pants' },
  ],
  Girls: [
    { key: 'All', label: 'All' },
    { key: 'Dresses', label: 'Dresses' },
    { key: 'Frocks', label: 'Frocks' },
    { key: 'Night Dress', label: 'Night Dress' },
    { key: 'Skirts', label: 'Skirts' },
  ],
  Ladies: [
    { key: 'All', label: 'All' },
    { key: 'Kurtis', label: 'Kurtis' },
    { key: 'Sarees', label: 'Sarees' },
    { key: 'Short Kurtis', label: 'Short Kurtis' },
    { key: 'T-Shirts', label: 'T-Shirts' },
    { key: 'Jeans', label: 'Jeans' },
    { key: 'Leggings', label: 'Leggings' },
    { key: 'Nighty', label: 'Nighty' },
  ],
  Men: [
    { key: 'All', label: 'All' },
    { key: 'T-Shirts', label: 'T-Shirts' },
    { key: 'Shirts', label: 'Shirts' },
    { key: 'Jeans', label: 'Jeans' },
    { key: 'Pants', label: 'Pants' },
    { key: 'Suits', label: 'Suits' },
    { key: 'Jackets', label: 'Jackets' },
    { key: 'Ethnic', label: 'Ethnic' },
  ],
};

const CategoryPage = () => {
  const { category } = useParams<{ category: string }>();
  const [sortBy, setSortBy] = useState('newest');
  const [selectedSubCategory, setSelectedSubCategory] = useState('All');
  const [page, setPage] = useState(0);
  
  const categoryKey = category?.charAt(0).toUpperCase() + category?.slice(1);
  const subCategories = SUB_CATEGORIES[categoryKey as keyof typeof SUB_CATEGORIES] || [];
  
  const { data: productsData, isLoading, error } = useProducts({
    category: categoryKey,
    subCategory: selectedSubCategory === 'All' ? undefined : selectedSubCategory,
    sort: sortBy,
    page,
    size: 20
  });

  const products = productsData?.content || [];
  const totalPages = productsData?.page?.totalPages || 0;

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">
              {categoryTitles[category || ''] || 'Products'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {productsData?.page?.totalElements || 0} products found
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="rating">Top Rated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Subcategory Filters */}
        {subCategories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-wrap gap-2">
              {subCategories.map((subCat) => (
                <Badge
                  key={subCat.key}
                  variant={selectedSubCategory === subCat.key ? "default" : "outline"}
                  className={`cursor-pointer px-4 py-2 text-sm hover:bg-primary hover:text-primary-foreground transition-colors ${
                    selectedSubCategory === subCat.key 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:border-primary'
                  }`}
                  onClick={() => {
                    setSelectedSubCategory(subCat.key);
                    setPage(0); // Reset to first page when changing subcategory
                  }}
                >
                  {subCat.label}
                </Badge>
              ))}
            </div>
          </motion.div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-lg">Loading products...</span>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-500 mb-4">Failed to load products</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        ) : products.length > 0 ? (
          <>
            {/* Products Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {products.map((product, index) => (
                <motion.div
                  key={product.productId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 py-2">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page === totalPages - 1}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg">No products found in this category.</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CategoryPage;
